// Bull and bear knocks behind the reeded glass (studio/ART-BIBLE.md, sprint 9 item 3).
//   bull : a head-on bull, two long horns sweeping out and up from a heavy brow; the horns are the widest thing.
//          Reveal: it lowers its head in three jumps, the horns swing out and fill the glass, a nose ring shows
//          and it snorts.
//   bear : a round head with two round ears (a clear gap between them) on a wide, low bulk of shoulders.
//          Reveal: it rears up in three jumps until its ears and bulk fill the glass; a pale muzzle, an open roar.
// A beam of the knock's own light (green for the bull, red for the bear) falls from the lamp above the glass and
// backlights the outline. It is a defined cone, not a wash, so it stays apart from an outbreak's green glass.
// Before the reveal a bull (or bear) looks the same whoever is behind it; at the reveal its eyes tell: an alien's
// go red, the infected's plague green, a human's stay plain.
// Pure; motion is stepped at 15 fps (every pose is a function of the frame number). drawGlass (visitors.js) paints it.
import { GLASS } from './visitors.js';
import { T } from './bits.js';
import { pickEye, eyeLook } from './eyes.js';

export const FPS = 15;
export const BEASTS = ['bull', 'bear'];
export const isBeast = s => s === 'bull' || s === 'bear';

const MW = 180, MH = 400, MCX = 90, MOY = 120, FEET = MOY + 250;
// reveal steps begin at these frames (after the lamp's stutter); 3 is the full pose
const STEPS = [3, 7, 11];
// how far the beam opens (glass px each side) at each reveal step: the bull's horns swing out to the glass edge and
// the lamp flares to keep their tips in the light. The bear's ears stay near the middle, so its cone barely opens and
// keeps its wedges as it roars. While knocking (step 0) both cones keep their ink wedges.
export const FLARE = { bull: [0, 6, 15, 24], bear: [0, 1, 2, 3] };

// The pose t seconds after the reveal; t < 0 (still knocking) is the same for every kind.
export function beastPose(beast, kind = null, t = -1) {
  const f = t == null || t < 0 ? -1 : Math.floor(t * FPS + 1e-6);
  const step = f < 0 ? 0 : STEPS.filter(s => f >= s).length;
  const eyes = f >= 8 && kind === 'alien' ? 'red' : f >= 8 && kind === 'infected' ? 'sick' : 'plain';
  if (beast === 'bull') {
    return { step, eyes, lower: step / 3, k: 1 + 0.07 * step, up: 0, detail: step >= 1,
      snort: f >= 13 && f < 25 ? f - 13 : -1 };
  }
  // the bear rears up, but its ears stay below the event plate
  return { step, eyes, lower: 0, k: 1 + 0.09 * step, up: 2 * step, detail: step >= 1, snort: -1 };
}

const maskCache = new Map();
// The figure as a mask, the same shape silhouetteMask (visitors.js) returns: 1 = the beast; d = detail shade
// (2 pale, 3 lit from behind) shown only from the first reveal step.
export function beastMask(beast, { lower = 0, detail = false } = {}) {
  const key = `${beast}|${lower}|${detail}`;
  if (maskCache.has(key)) return maskCache.get(key);
  const m = new Uint8Array(MW * MH), d = new Uint8Array(MW * MH);
  const ell = (x, y, cx, cy, ax, ay) => ((x - cx) / ax) ** 2 + ((y - cy) / ay) ** 2 <= 1;
  let M;
  if (beast === 'bull') {
    const hx = MCX, hy = MOY + Math.round(12 * lower);
    // the horns: a quadratic curve from the brow out and up; lowering the head swings the tips out and down
    const horn = [];
    // thick at the root and still 7 px across at the tip, so the reeded glass cannot blur the points away
    for (let i = 0; i <= 90; i++) {
      const t = i / 90, a = (1 - t) ** 2, b = 2 * t * (1 - t), c = t * t;
      const x = a * 22 + b * (68 + 6 * lower) + c * (50 + 16 * lower);
      const y = a * -12 + b * (-6 + 8 * lower) + c * (-48 + 24 * lower);
      horn.push([x, y, 8.5 - 5 * t]);
    }
    for (let y = 0; y < MH; y++) for (let x = 0; x < MW; x++) {
      const dx = x - hx, i = y * MW + x;
      let on = ell(x, y, hx, hy, 25, 20); // the skull
      on ||= Math.abs(dx) < 28 && y >= hy - 15 && y < hy - 2; // a heavy, low brow
      if (y >= hy && y < hy + 40) on ||= Math.abs(dx) < 22 - (y - hy) * 0.1; // the long face
      on ||= ell(x, y, hx, hy + 38, 21, 10); // a broad snout
      for (const sx of [-1, 1]) on ||= ell(x, y, hx + sx * 34, hy + 3 + (Math.abs(dx) - 26) * 0.25, 11, 5.5); // ears, out sideways and down
      on ||= ell(x, y, MCX, 252, 54, 124); // the shoulders: a hump behind the head, narrower than the horns
      if (!on) for (const [hxo, hyo, r] of horn) {
        if (Math.abs(Math.abs(dx) - hxo) <= r && Math.abs(y - hy - hyo) <= r && Math.hypot(Math.abs(dx) - hxo, y - hy - hyo) <= r) { on = true; break; }
      }
      if (!on) continue;
      m[i] = 1;
      if (detail) {
        const ring = Math.hypot(dx, y - (hy + 39)); // a nose ring through the snout, hanging below the nostrils
        if (y > hy + 38 && ring >= 5 && ring < 7.5) d[i] = 3;
      }
    }
    M = { head: [hx, hy, 25, 20], mouth: [hx, hy + 40], eyes: [[hx - 15, hy - 2], [hx + 15, hy - 2]],
      nostrils: [[hx - 9, hy + 38], [hx + 9, hy + 38]] };
  } else {
    const hx = MCX, hy = MOY - 2;
    for (let y = 0; y < MH; y++) for (let x = 0; x < MW; x++) {
      const i = y * MW + x;
      let on = ell(x, y, hx, hy, 30, 27); // a round head
      on ||= ell(x, y, hx, hy + 10, 35, 20); // full cheeks: wider low down
      for (const sx of [-1, 1]) on ||= Math.hypot(x - (hx + sx * 25), y - (hy - 24)) <= 11; // round ears
      on ||= ell(x, y, MCX, 250, 96, 118); // a wide, low bulk of shoulders
      if (!on) continue;
      m[i] = 1;
      if (detail) {
        if ([-1, 1].some(sx => Math.hypot(x - (hx + sx * 25), y - (hy - 23)) <= 5.5)) d[i] = 3; // inside the ears
        if (ell(x, y, hx, hy + 13, 14, 11)) d[i] = 3; // a pale muzzle
        if (ell(x, y, hx, hy + 7, 5, 3)) d[i] = 0; // the nose
        if (ell(x, y, hx, hy + 18, 7, 4)) d[i] = 0; // an open roar
      }
    }
    M = { head: [hx, hy, 30, 27], mouth: [hx, hy + 18], eyes: [[hx - 13, hy - 4], [hx + 13, hy - 4]], nostrils: [] };
  }
  let topRow = MH;
  for (let i = 0; i < m.length; i++) if (m[i]) { topRow = Math.floor(i / MW); break; }
  Object.assign(M, { w: MW, h: MH, m, d, cx: MCX, oy: MOY, top: topRow, feet: FEET });
  if (maskCache.size > 32) maskCache.clear();
  maskCache.set(key, M);
  return M;
}

// Everything drawGlass needs for one frame of a bull or bear knock (the same fields glassState returns, plus the beam).
// kind and verdict: who was really behind the glass and the call, once revealed (revealT >= 0).
export function beastState({ beast, kind = null, revealT = -1, fr = 0, t = 0, outbreak = false, lamp = 1, approach = 1,
  jitter = 0, blink = false, eye = null, slip = false, redEyes = false } = {}) {
  const pose = beastPose(beast, kind, revealT);
  const M = beastMask(beast, { lower: pose.lower, detail: pose.detail });
  const base = 0.62 + 0.38 * approach;
  const s = base * pose.k;
  const sway = Math.sin(t * 1.3) * 0.8 + jitter;
  const gs = { lamp, fr, t, outbreak: !!outbreak, glitch: false, snow: 1, ground: false, glow: 0, sores: false, hidden: false,
    M, sx: s, sy: s, ax: GLASS.hx + sway, hv: GLASS.foot - (GLASS.foot - GLASS.hy) * base - pose.up, pres: 0.32 + 0.68 * approach,
    crisp: false, eyes: [], eyesOn: false, eyeTint: T.none, puffs: [], prints: [], puffTint: T.none,
    beam: { beast, tint: beast === 'bull' ? T.bull : T.bear, flare: FLARE[beast][pose.step] } };
  // the head's centre lands on a whole glass pixel, so the eyes are level and mirror-symmetric
  const [hx] = M.head;
  const c0 = gs.ax + (hx - M.cx) * s, c = Math.round(c0);
  gs.ax += c - c0;
  gs.eyes = M.eyes.map(([x, y]) => { const dd = (x - hx) * s; return [c + Math.sign(dd) * Math.round(Math.abs(dd)), Math.round(gs.hv + (y - M.oy) * s)]; });
  gs.eyesOn = gs.pres > 0.85 && !blink;
  gs.eyeTint = pose.eyes === 'red' || redEyes ? T.red : pose.eyes === 'sick' || outbreak ? T.green : T.none;
  gs.eye = pickEye('S', eye, eyeLook(gs.eyeTint === T.red, pose.eyes === 'sick', kind, revealT), slip);
  if (pose.snort >= 0) { // two paper puffs of breath from the nostrils, out and down
    const a = pose.snort;
    gs.puffs = M.nostrils.map(([x, y], j) => {
      const sx = j ? 1 : -1;
      return { u: gs.ax + (x - M.cx + sx * (6 + a * 4)) * s, v: gs.hv + (y - M.oy + 6 + a * 1.5) * s, r: Math.max(2, Math.round((4 + a * 0.9) * s)), dens: 1 - a / 12 };
    });
  }
  return gs;
}
